import { usePostHogEvent } from "@/lib/posthog";
import { colorsV2 } from "@somethings-ui/core";
import type { ConnectQuestion } from "@somethings-ui/core/connect-questions";
import { useCallback, useEffect, useRef } from "react";
import { PeriwinkleCard } from "./periwinkle-card";
import { Button } from "@somethings-ui/web";
import { CONNECT_CARDSET_ID } from "./use-connect-rows";

const DIVIDER = "#f2f0ec";

interface ConnectMomentIntroStepProps {
  mentorshipId: string;
  questionIds: string[];
  questions: [ConnectQuestion, ConnectQuestion, ConnectQuestion];
  onContinue: () => void;
}

export function ConnectMomentIntroStep({
  mentorshipId,
  questionIds,
  questions,
  onContinue,
}: ConnectMomentIntroStepProps) {
  const { dispatchEvent } = usePostHogEvent("activity.mentor");
  const openedAtRef = useRef(performance.now());

  useEffect(() => {
    dispatchEvent("mentor_chat_connect_intro_view", {
      mentorshipId,
      cardsetId: CONNECT_CARDSET_ID,
      questionIds,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = useCallback(() => {
    dispatchEvent("mentor_chat_connect_intro_continue", {
      mentorshipId,
      cardsetId: CONNECT_CARDSET_ID,
      questionIds,
      timeOnScreenMs: Math.round(performance.now() - openedAtRef.current),
    });
    onContinue();
  }, [dispatchEvent, mentorshipId, questionIds, onContinue]);

  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 pb-3 pt-6">
        <ConnectIntroPreview questions={questions} />
        <p
          className="mt-6 px-2 text-center text-sm leading-relaxed"
          style={{ color: colorsV2.text.secondary }}
        >
          Answer all three out loud. Swap any you don&apos;t feel. Send the set
          when it&apos;s ready.
        </p>
      </div>
      <div
        className="flex items-center justify-between gap-3 border-t px-6 py-4"
        style={{ borderColor: DIVIDER }}
      >
        <span
          className="font-mono text-xs tabular-nums"
          style={{ color: colorsV2.text.secondary }}
        >
          3 questions · 2 minutes
        </span>
        <Button onClick={handleContinue} size="lg">
          Continue
        </Button>
      </div>
    </>
  );
}

function ConnectIntroPreview({
  questions,
}: {
  questions: [ConnectQuestion, ConnectQuestion, ConnectQuestion];
}) {
  const tilts = [-12, 0, 12];
  return (
    <div className="relative mx-auto h-[260px] w-[440px]">
      {questions.map((q, i) => {
        const tilt = tilts[i];
        const left = i * 120 + 21;
        return (
          <PeriwinkleCard
            key={q.id}
            variant="highlight"
            className="absolute h-[224px] w-[158px] rounded-2xl border border-white/80"
            style={{
              top: 18,
              left,
              transform: `rotate(${tilt}deg)`,
              zIndex: i,
            }}
          >
            <div className="flex h-full flex-col p-4">
              <span className="font-mono text-base text-white/90">
                {`0${i + 1}`}
              </span>
              <p className="mt-auto line-clamp-3 text-lg font-bold leading-[1.1] text-white">
                {q.text}
              </p>
            </div>
          </PeriwinkleCard>
        );
      })}
    </div>
  );
}
